/**
 * Frontend feature flags, delivered by the edge inside the session settings
 * (settings.feature_flags — evaluated server-side by dcams-get-session via
 * the Cloudflare Flagship binding, sticky per request_id).
 *
 * Module-level singleton (like telemetry.js) so non-React modules — e.g.
 * useFaceDetection's module-scope preload — can read flags too. Every
 * accessor fails safe: missing settings, missing flag, or any error
 * resolves to the fallback, which must always be the legacy behavior.
 */

// Retired experiments (winners shipped 2026-09-14, flags can be deleted
// from Flagship and dcams-get-session FRONTEND_FLAGS):
//   enable-face-capture-opimization  -> optimized face capture is the only path
//   enable-face-capture-hold-v2      -> auto-capture hold is 1800ms everywhere
//   enable-doc-upload-compression    -> Microblink uploads encode at JPEG 0.87

// A/B: replace the face inference engine. Control is face-api.js 0.22.2
// (unmaintained since 2020, pinned to tfjs-core 1.7.0). Its WebGL shader
// compile is the largest wait in the flow — the start button is gated on
// modelsReady, so landed -> models_ready runs 12.5s p50 / 34.9s p95 on
// iOS with the user on a disabled button the whole time. The test arm is
// MediaPipe Tasks Vision (BlazeFace short-range) on WASM: no shader
// compile, and ~3.7MB gzipped of runtime + model to fetch instead.
// Default false = face-api, i.e. today's behavior.
export const FACE_ENGINE_MEDIAPIPE = "enable-mediapipe-face-engine";

// A/B: best-frame capture. During the 1800ms auto-capture hold every
// frame already passes all five gates; the test arm keeps the sharpest,
// best-lit frame seen in that window and uploads it instead of whatever
// frame is current when the timer fires. No new gate, no added wait.
// Capture-frame telemetry (2026-09-16) tied liveness failure to
// sharpness (blur < 300: 18-45% fail; > 800: 1.4-5.8%) and face luma
// (< 80: 25-40%). A continuous sharpness floor (#392) choked Android
// because low-end sensors flicker around any threshold; picking the
// best of the frames already accepted cannot. Default false = current
// behavior. Applies on top of either face engine.
export const FACE_BEST_FRAME = "enable-best-frame-capture";

// Rollout: upload images through a presigned PUT URL fetched from
// vx.dcams.app/v1/upload-url instead of minting anonymous AWS credentials
// from the unauthenticated Cognito identity pool in the browser (the
// 2026-08 S3 exposure ran through that pool's role). The test arm needs
// settings.upload_token from dcams-get-session; without it, or on any
// failure of the presigned path, uploadImageToS3 falls back to Cognito.
// Default false = Cognito. Once at 100% the pool, its role and the
// bucket's public-write policy are retired together.
export const PRESIGNED_UPLOAD = "enable-presigned-upload";

// A/B: the refreshed Veratad ID document capture screen (capture-v2).
// Control is the original manual capture: full-viewport feed with an
// SVG mask cutout, a Mantine review drawer and a fixed flip-transition
// screen, portrait only (the app-wide rotate-your-device prompt covers
// it sideways). The test arm is the "ID Capture Variants" design,
// variant A: a dark chrome with a step header, corner-bracket guide,
// in-portal review and turn-over hint, a landscape layout, and light
// image-quality gates (brightness, blur, glare) that hold the shutter
// until the frame is usable. Every id_doc_* event carries
// `capture_ui: "v1" | "v2"` so the funnels split without a join.
// Default false = v1, i.e. today's behavior.
export const ID_DOC_CAPTURE_V2 = "enable-id-doc-capture-v2";

// Kill switch for best-frame capture on the v2 ID document screen. In
// assisted mode every frame that clears the light quality gates is a
// valid capture; the shutter uploads the sharpest, best-lit frame from
// the last two seconds when it beats the current one (same idea as
// FACE_BEST_FRAME). Default true = on wherever v2 runs; only read
// through flagEnabled(..., true) so an absent flag keeps it on.
export const ID_DOC_BEST_FRAME = "enable-id-doc-best-frame";

// Kill switch for countdown auto-capture on the v2 ID document screen.
// Once the four quality gates and the document-presence heuristic have
// held for 2s, a 3-2-1 countdown runs on the shutter and fires the
// capture; a tap takes it sooner, movement cancels it. Default true =
// on wherever v2 runs; read through flagEnabled(..., true).
export const ID_DOC_AUTO_CAPTURE = "enable-id-doc-auto-capture";

// A/B (shadow): read the machine-readable data off each ID document
// photo in the browser. On arm: after the shutter, a worker tries the
// PDF417 barcode (zxing-wasm, plus the native BarcodeDetector where the
// browser has one) and the MRZ (Tesseract.js with an OCR-B model on a
// deskewed crop, validated by check digits), and one id_doc_machine_read
// event reports whether each was read and how long it took. No decoded
// text is reported and nothing the user sees or uploads changes; the
// test measures the read rate on production photos and whether the
// extra work costs completion or time. Every event from the Veratad
// capture flow carries `machine_read: true | false`.
// ?force_machine_read=true|false pins it for a session.
// Default false = no read, i.e. today's behavior.
export const ID_DOC_MACHINE_READ = "enable-id-doc-machine-read";

// Preview: landscape support. When on, the app-wide rotate-your-device
// prompt never opens, the Veratad face capture screen lays out sideways
// (ported from vx-capture-sdk: landscape is layout only, same camera
// request, engine, gates and full-frame output) and the age-estimation
// start drawer becomes a side panel. Not in Flagship yet: today it is
// only switched on with ?face_landscape=true (see landscapeEnabled).
// Default false = portrait only, i.e. today's behavior.
export const FACE_LANDSCAPE = "enable-face-landscape";

const flags = {};
let known = false;
const waiters = [];

export const setFeatureFlags = (next) => {
  try {
    if (next && typeof next === "object") {
      Object.assign(flags, next);
      known = true;
      waiters.splice(0).forEach((resolve) => resolve(true));
    }
  } catch {
    // never throw
  }
};

/**
 * Resolves true once the edge-delivered flags have arrived, or false
 * after `timeoutMs` so a session that never receives flags (legacy,
 * non-edge) still proceeds on defaults. Use this anywhere a decision
 * must not be made on a guess: a capture screen can mount (child
 * effects) before DataContext publishes the flags (parent effects),
 * so "flags are set by now" is not a safe assumption on first render.
 */
export const whenFlagsKnown = (timeoutMs = 3000) => {
  if (known) return Promise.resolve(true);
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(false), timeoutMs);
    waiters.push(() => {
      clearTimeout(t);
      resolve(true);
    });
  });
};

// True once the edge-delivered flags have arrived. Code that must not
// guess a variant (e.g. telemetry emits) can defer until this flips.
export const flagsKnown = () => known;

export const flagEnabled = (name, fallback = false) =>
  typeof flags[name] === "boolean" ? flags[name] : fallback;

// Face capture for the face-to-ID-document match, v2: the same intro and
// capture experience as age estimation (camera view mounted from the start
// with the start drawer over it, side panel when held sideways under
// FACE_LANDSCAPE), copy that is not age specific, and the v1 upload /
// integrity / hand-back logic unchanged. Default false = v1 (the static
// intro page). ?force_face_match_v2=true|false pins it for a session.
export const FACE_MATCH_CAPTURE_V2 = "enable-face-match-capture-v2";

export const faceMatchV2Enabled = () => {
  try {
    const forced = new URLSearchParams(window.location.search).get("force_face_match_v2");
    if (forced === "true") return true;
    if (forced === "false") return false;
  } catch {
    // fall through to the flag
  }
  return flagEnabled(FACE_MATCH_CAPTURE_V2);
};

/**
 * FACE_LANDSCAPE with its QA override: ?face_landscape=true|false pins
 * it and wins over the flag. Cheap enough to call on every render.
 */
export const landscapeEnabled = () => {
  try {
    const forced = new URLSearchParams(window.location.search).get("face_landscape");
    if (forced === "true") return true;
    if (forced === "false") return false;
  } catch {
    // fall through to the flag
  }
  return flagEnabled(FACE_LANDSCAPE);
};

/**
 * Numeric flag (a tunable rather than a switch), e.g. a quality-gate
 * threshold. Accepts a number or a numeric string; anything else, or a
 * missing flag, resolves to the fallback.
 */
export const flagNumber = (name, fallback = null) => {
  const v = flags[name];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return fallback;
};

// Tunables for the v2 ID document quality gates (assisted strategy).
// Each overrides one threshold when set as a numeric Flagship flag; the
// journey setting docCaptureQuality and the ?doc_quality= URL param can
// set the same values (see resolveQualityOverrides in the capture flow).
// The effective values and where each came from are stamped on every
// id_doc_quality_* and id_doc_captured event.
export const ID_DOC_QUALITY_FLAGS = {
  blurMin: "id-doc-quality-blur-min",       // Laplacian variance floor inside the frame (all platforms)
  blurMinAndroid: "id-doc-quality-blur-min-android", // same, Android only (wins over blurMin)
  blurMinIos: "id-doc-quality-blur-min-ios",         // same, iOS only (wins over blurMin)
  glareMax: "id-doc-quality-glare-max",     // max fraction of near-white pixels (0-1)
  glareMaxBack: "id-doc-quality-glare-max-back", // same ceiling for the back side only (judged on clipped pixels)
  lumaMin: "id-doc-quality-luma-min",       // mean luma floor inside the frame (0-255)
  lumaMax: "id-doc-quality-luma-max",       // mean luma ceiling inside the frame (0-255)
  lumaStdMin: "id-doc-quality-luma-std-min", // contrast floor (luma std dev)
  darkRoom: "id-doc-quality-dark-room",     // whole-frame brightness floor (0-255)
  motionMax: "id-doc-quality-motion-max",   // max frame-to-frame change in the motion reading (steady gate)
  holdMs: "id-doc-quality-hold-ms",         // max time the shutter is held (ms)
};

/**
 * ID_DOC_MACHINE_READ with its QA override. Takes the session settings
 * when the caller has them (the flag object may not be published to the
 * module yet on first render).
 */
export const machineReadEnabled = (settings) => {
  try {
    const forced = new URLSearchParams(window.location.search).get("force_machine_read");
    if (forced === "true") return true;
    if (forced === "false") return false;
  } catch {
    // fall through to the flag
  }
  const fromSettings = settings?.feature_flags?.[ID_DOC_MACHINE_READ];
  if (typeof fromSettings === "boolean") return fromSettings;
  return flagEnabled(ID_DOC_MACHINE_READ);
};
